'use client';

import React, { memo, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArrowBackIcon, ArrowForwardIcon } from '@/shared/ui/display/EmojiIcon';
import { usePagination } from './hooks/usePagination';
import { usePaginationTheme } from './theme';
import type { UsePaginationOptions } from './hooks/usePagination';
import { TabLink } from '@/shared/ui/navigation/TabLink';
import { usePathname, useSearchParams } from 'next/navigation';
import { deserialize, serialize } from '@/entities/tab/model/path';
import type { TabContext } from '@/entities/tab/model/tab.types';
import { useUpdateTabParams } from '@/features/tab/tab-history';

interface IProps extends Omit<UsePaginationOptions, 'pageSize'> {
  initialPageSize?: number;
  showLabel?: boolean;
  labels?: {
    previous?: string;
    next?: string;
  };
  /** 탭 컨텍스트 (탭별 독립적인 상태를 위해 사용) */
  tabContext?: TabContext;
}

function PaginationComponent({
  initialCursor = null,
  initialPageSize = 10,
  hasPreviousPage,
  hasNextPage,
  nextCursor,
  total,
  onPageChange,
  onPageSizeChange,
  queryKey = 'page',
  showLabel = true,
  labels = {},
  tabContext,
}: IProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // 탭 컨텍스트가 있으면 탭 params 사용, 없으면 URL 사용
  const tabParams = tabContext?.params;
  const { updateTabParams } = useUpdateTabParams();
  const handleTabParamsUpdate = (params: Record<string, unknown>) => {
    if (tabContext?.tabId) {
      updateTabParams(tabContext.tabId, params);
    }
  };

  const { cursor, currentPageIndex, goToPrevious, goToNext, isFirstPage, isLastPage, pageCursors } =
    usePagination({
      initialCursor,
      pageSize: initialPageSize,
      hasPreviousPage,
      hasNextPage,
      nextCursor,
      total,
      onPageChange,
      onPageSizeChange,
      queryKey,
      tabParams,
      onTabParamsUpdate: tabContext ? handleTabParamsUpdate : undefined,
    });

  const { getContainerSx, getButtonSx, getLabelSx } = usePaginationTheme();

  const previousLabel = labels.previous || '이전';
  const nextLabel = labels.next || '다음';

  // 현재 TabContext 가져오기: 탭 컨텍스트가 있으면 사용, 없으면 URL에서 가져오기
  const currentContext = useMemo<TabContext | null>(() => {
    if (tabContext) {
      return tabContext;
    }
    try {
      const currentUrl = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
      return deserialize(currentUrl);
    } catch {
      return null;
    }
  }, [pathname, searchParams, tabContext]);

  // 이전 페이지 URL 생성
  const previousPageUrl = useMemo<string | null>(() => {
    if (isFirstPage || !currentContext) return null;

    const previousIndex = currentPageIndex - 1;
    const previousPageInfo = pageCursors.find((p) => p.pageNumber === previousIndex);

    if (!previousPageInfo) {
      // 첫 페이지로 이동
      const newContext: TabContext = {
        ...currentContext,
        params: {
          ...currentContext.params,
        },
      };
      delete newContext.params[queryKey];
      delete newContext.params[`${queryKey}Index`];
      return serialize(newContext);
    }

    const newContext: TabContext = {
      ...currentContext,
      params: {
        ...currentContext.params,
      },
    };

    if (previousPageInfo.cursor) {
      newContext.params[queryKey] = previousPageInfo.cursor;
    } else {
      delete newContext.params[queryKey];
    }

    if (previousIndex > 1) {
      newContext.params[`${queryKey}Index`] = previousIndex.toString();
    } else {
      delete newContext.params[`${queryKey}Index`];
    }

    return serialize(newContext);
  }, [isFirstPage, currentContext, currentPageIndex, pageCursors, queryKey]);

  // 다음 페이지 URL 생성
  const nextPageUrl = useMemo<string | null>(() => {
    if (isLastPage || !hasNextPage || !nextCursor || !currentContext) return null;

    const newIndex = currentPageIndex + 1;
    const newContext: TabContext = {
      ...currentContext,
      params: {
        ...currentContext.params,
        [queryKey]: nextCursor,
        [`${queryKey}Index`]: newIndex.toString(),
      },
    };

    return serialize(newContext);
  }, [isLastPage, hasNextPage, nextCursor, currentContext, currentPageIndex, queryKey]);

  return (
    <Box sx={getContainerSx()}>
      {showLabel && <Typography sx={getLabelSx()}>페이지 {currentPageIndex}</Typography>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {previousPageUrl && !isFirstPage ? (
          <TabLink
            to={previousPageUrl}
            onClick={() => {
              // TabLink 클릭 시에도 onPageChange 호출
              const previousIndex = currentPageIndex - 1;
              const previousPageInfo = pageCursors.find((p) => p.pageNumber === previousIndex);
              onPageChange?.({ cursor: previousPageInfo?.cursor ?? null });
            }}
            style={{
              display: 'inline-flex',
              textDecoration: 'none',
            }}
          >
            <Box
              component="span"
              sx={{
                ...getButtonSx(isFirstPage),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: isFirstPage ? 'not-allowed' : 'pointer',
                pointerEvents: isFirstPage ? 'none' : 'auto',
              }}
            >
              <ArrowBackIcon sx={{ fontSize: '1rem' }} />
              {previousLabel}
            </Box>
          </TabLink>
        ) : (
          <Button
            variant="outlined"
            size="small"
            disabled={isFirstPage}
            startIcon={<ArrowBackIcon />}
            sx={getButtonSx(isFirstPage)}
            onClick={goToPrevious}
          >
            {previousLabel}
          </Button>
        )}

        <Typography
          sx={{
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {currentPageIndex}
        </Typography>

        {nextPageUrl && !isLastPage ? (
          <TabLink
            to={nextPageUrl}
            onClick={() => {
              // TabLink 클릭 시에도 onPageChange 호출
              onPageChange?.({ cursor: nextCursor ?? null });
            }}
            style={{
              display: 'inline-flex',
              textDecoration: 'none',
            }}
          >
            <Box
              component="span"
              sx={{
                ...getButtonSx(isLastPage),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: isLastPage ? 'not-allowed' : 'pointer',
                pointerEvents: isLastPage ? 'none' : 'auto',
              }}
            >
              {nextLabel}
              <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
            </Box>
          </TabLink>
        ) : (
          <Button
            variant="outlined"
            size="small"
            disabled={isLastPage}
            endIcon={<ArrowForwardIcon />}
            sx={getButtonSx(isLastPage)}
            onClick={goToNext}
          >
            {nextLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export const Pagination = memo(PaginationComponent);
